import React, { useEffect, useState } from 'react';
import rightArrow from '../../assets/right-double-arrow.svg';
import { useUserContext } from '../../contexts/UserContext';
import { useContentfulContext } from '../../contexts/ContentfulContext';
import { getSideNavItems } from '../../config/sidenav';
import SidebarItem from './SidenavItem';
import rightSingleArrow from '../../assets/icons/right-arrow.svg';
import ManageAccountSelectBox from '../OrganizationSelectBox/ManageOrganisationSelectBox';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { BookText } from 'lucide-react';
import { usePreviewMode } from '../../contexts/PreviewModeContext';
import { X } from 'lucide-react';
import { useUIContext } from '../../contexts/UIContext';
import { permission } from '../../config/permission';

const Sidenav = () => {
  const [isSideBarExpand, setIsSideBarExpand] = useState(false);
  const [showDropdown, setShowDropdown] = useState(null);
  const [sidebar_features, setSidebar_features] = useState([]);
  const [filteredFeatures, setFilteredFeatures] = useState([]);

  const { content } = useContentfulContext();
  const { userData, appData, checkPermission } = useUserContext();
  const navigate = useNavigate();
  const { showSidebar, showNavbar } = usePreviewMode();
  const { isMobileSidebarOpen, closeMobileSidebar } = useUIContext();
  const location = useLocation();

  useEffect(() => {
    setSidebar_features(getSideNavItems(content?.LEFT_SIDE_NAV_OPTIONS, "main"));
  }, [])

  const toggleSideBar = () => {
    setIsSideBarExpand(!isSideBarExpand);
  };

  const handleTutorials = () => {
    navigate('/tutorials');
  };

  const handleToggle = (dropdown) => {
    setShowDropdown(showDropdown === dropdown ? null : dropdown);
  };

  const filterSidebarFeatures = (features, route_permission) => {
    if (!userData?.selectedOrganization) return;

    return features?.map(feature => {
      // EPM/XCM children filtered by feature permissions
      if (feature.id === 'epm') {
        const permittedChildren = feature?.children?.filter(child => {
          if (child.id === 'epm-testing-board') {
            return checkPermission(permission.XCM_TESTS_VIEW);
          }
          if (child.id === 'epm-schedule') {
            return checkPermission(permission.XCM_SCHEDULE_VIEW);
          }
          if (child.id === 'epm-learnings') {
            return checkPermission(permission.XCM_LEARNINGS_VIEW);
          }
          if (child.id === 'epm-settings') {
            return checkPermission(permission.XCM_SETTINGS_VIEW);
          }
          return true; // epm-home remains visible
        });

        return { ...feature, children: permittedChildren };
      }

      if (feature?.children) {
        const permittedChildren = feature?.children?.filter(child => route_permission[child.id]);
        if (permittedChildren.length > 0) {
          return { ...feature, children: permittedChildren };
        }
      }

      return route_permission[feature.id] ? feature : null;
    }).filter(feature => feature !== null);
  };

  useEffect(() => {
    if (isSideBarExpand) {
      setShowDropdown(null);
    }
  }, [isSideBarExpand])

  useEffect(() => {
    if (!isSideBarExpand && filteredFeatures?.length > 0) {
      const activeParent = filteredFeatures.find(item =>
        item?.children?.some(child => location.pathname === child.route)
      );
      if (activeParent) {
        setShowDropdown(activeParent.id);
      }
    }
  }, [location.pathname, filteredFeatures, isSideBarExpand])

  useEffect(() => {
    if (sidebar_features?.length) {
      setFilteredFeatures(filterSidebarFeatures(sidebar_features, userData?.selectedOrganization?.level?.route_permission));
    }
  }, [sidebar_features, userData?.selectedOrganization?.level?.route_permission, userData?.selectedOrganization?.permissions])

  useEffect(() => {
    if (!showSidebar) {
      setIsSideBarExpand(true);
    }
  }, [showSidebar])

  return (
    <>
      {/* Mobile Overlay */}
      {isMobileSidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={closeMobileSidebar}
        />
      )}
      <aside className={`
        ${isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'} 
        lg:translate-x-0 
        fixed lg:sticky 
        top-0 lg:top-[58px] 
        left-0 
        z-50 lg:z-30 
        transition-all duration-300 ease-in-out
        ${isSideBarExpand ? 'w-[64px]' : 'w-72'} 
        ${!showNavbar ? "h-screen" : "md:h-[calc(100vh-58px)] h-screen"} 
        bg-white lg:bg-surface-subtle
        border-r border-border
        overflow-hidden
      `}>
        <div className="flex flex-col h-full bg-white lg:bg-surface-subtle text-sm">
          <div className="lg:hidden flex justify-end p-2">
            <button onClick={closeMobileSidebar} className="p-1 hover:bg-gray-100 rounded-lg">
              <X size={20} />
            </button>
          </div>
          <div className={`space-y-1.5 ${isSideBarExpand ? 'px-2' : 'px-2.5'} py-3 flex flex-col justify-between h-full overflow-y-auto overflow-x-hidden`}>
            <div className="space-y-1">
              {/* Mobile Global Selectors */}
              <div className="ss:hidden flex flex-col gap-3 px-1 mb-3 pb-3 border-b border-slate-200">
                {userData?.selectedOrganization && (
                  <div className="w-full h-10 flex items-center justify-center">
                    <ManageAccountSelectBox />
                  </div>
                )}
              </div>

              {filteredFeatures?.length > 0 && filteredFeatures.map(item => (
                <SidebarItem key={item.id} item={item} showDropdown={showDropdown} handleToggle={handleToggle} isSideBarExpand={isSideBarExpand} />
              ))}
            </div>
            <div className="pt-3 border-t border-slate-200/60 space-y-1">
              <div title={isSideBarExpand ? content?.LEFT_SIDE_NAV_OPTIONS?.USEFUL_LINKS : undefined}>
                <a
                  href="#"
                  className={`flex items-center rounded-xl transition-colors text-xs md:text-sm font-medium group text-slate-600 hover:text-slate-900 hover:bg-slate-100/70 ${
                    isSideBarExpand ? 'w-10 h-10 mx-auto justify-center p-0' : 'px-2.5 py-2'
                  }`}
                  onClick={(e) => {
                    e.preventDefault();
                    if (!isSideBarExpand) {
                      handleToggle('Useful Links');
                    }
                  }}
                >
                  <img src={"https://stridetech.io/wp-content/uploads/2025/02/Stride_UsefulLinks.svg"} alt='->' className='flex-shrink-0 w-5 h-5 text-gray-500 transition duration-75 group-hover:text-gray-900' />
                  {!isSideBarExpand && (
                    <span className="flex-1 ml-3 whitespace-nowrap flex items-center justify-between">
                      {content?.LEFT_SIDE_NAV_OPTIONS?.USEFUL_LINKS}
                      <p className={`text-xl transition-transform ${showDropdown == 'Useful Links' ? 'rotate-90' : ''}`} >
                        <img src={rightSingleArrow} alt="" className='w-5 h-5' />
                      </p>
                    </span>
                  )}
                </a>
                {!isSideBarExpand && showDropdown == 'Useful Links' && <div className='ml-3 pl-2.5 my-1 space-y-0.5 border-l-2 border-slate-200/80'>
                  <a href="https://experienceleague.adobe.com/tools/calculator/testcalculator.html"
                    target='_blank'
                    className="flex items-center px-3 py-1.5 text-slate-600 hover:text-slate-900 rounded-lg hover:bg-slate-100/70 transition-colors text-xs md:text-[13px] font-medium"
                    rel="noreferrer"
                    onClick={closeMobileSidebar}
                  >
                    <span className="flex-1 truncate">{content?.LEFT_SIDE_NAV_OPTIONS?.SAMPLE_SIZE_CALCULATOR}</span>
                  </a>
                  <a href="https://cxl.com/ab-test-calculator/"
                    target='_blank'
                    className="flex items-center px-3 py-1.5 text-slate-600 hover:text-slate-900 rounded-lg hover:bg-slate-100/70 transition-colors text-xs md:text-[13px] font-medium"
                    rel="noreferrer"
                    onClick={closeMobileSidebar}
                  >
                    <span className="flex-1 truncate">{content?.LEFT_SIDE_NAV_OPTIONS?.TEST_ANALYSIS}</span>
                  </a>
                </div>}
              </div>
              <div title={content?.LEFT_SIDE_NAV_OPTIONS?.TUTORIALS}>
                <a
                  href="#"
                  className={`flex items-center rounded-xl hover:bg-slate-100/70 transition-colors text-xs md:text-sm font-medium group text-slate-600 hover:text-slate-900 ${
                    isSideBarExpand ? 'w-10 h-10 mx-auto justify-center p-0' : 'px-2.5 py-2'
                  }`}
                  onClick={(e) => {
                    e.preventDefault();
                    handleTutorials();
                    closeMobileSidebar();
                  }}
                >
                  <img src={"https://stridetech.io/wp-content/uploads/2025/02/Stride_ProductGuides.svg"} alt='->' className='flex-shrink-0 w-5 h-5 text-gray-500 transition duration-75 group-hover:text-gray-900' />
                  {!isSideBarExpand && (
                    <span className="flex-1 ml-3 whitespace-nowrap">{content?.LEFT_SIDE_NAV_OPTIONS?.TUTORIALS}</span>
                  )}
                </a>
              </div>
              <div title={content?.LEFT_SIDE_NAV_OPTIONS?.DOCUMENTATION?.title}>
                <a href={content?.LEFT_SIDE_NAV_OPTIONS?.DOCUMENTATION?.redirect_link}
                  target='_blank'
                  rel="noreferrer"
                  className={`flex items-center rounded-xl hover:bg-slate-100/70 transition-colors text-xs md:text-sm font-medium group text-slate-600 hover:text-slate-900 ${
                    isSideBarExpand ? 'w-10 h-10 mx-auto justify-center p-0' : 'px-2.5 py-2'
                  }`}
                  onClick={closeMobileSidebar}
                >
                  <div className='flex-shrink-0 w-5 h-5 flex items-center justify-center text-gray-500 transition duration-75 group-hover:text-gray-900'>
                    <BookText strokeWidth={1} size={20} />
                  </div>
                  {!isSideBarExpand && (
                    <span className="flex-1 ml-3 whitespace-nowrap">{content?.LEFT_SIDE_NAV_OPTIONS?.DOCUMENTATION?.title}</span>
                  )}
                </a>
              </div>
              <div title={content?.LEFT_SIDE_NAV_OPTIONS?.HELP}>
                <a
                  href="#"
                  className={`flex items-center rounded-xl hover:bg-slate-100/70 transition-colors text-xs md:text-sm font-medium group text-slate-600 hover:text-slate-900 ${
                    isSideBarExpand ? 'w-10 h-10 mx-auto justify-center p-0' : 'px-2.5 py-2'
                  }`}
                  onClick={(e) => e.preventDefault()}
                >
                  <img src={"https://stridetech.io/wp-content/uploads/2025/02/Help.svg"} alt='->' className='flex-shrink-0 w-5 h-5 text-gray-500 transition duration-75 group-hover:text-gray-900' />
                  {!isSideBarExpand && (
                    <span className="flex-1 ml-3 whitespace-nowrap">{content?.LEFT_SIDE_NAV_OPTIONS?.HELP}</span>
                  )}
                </a>
              </div>
              <div title={content?.LEFT_SIDE_NAV_OPTIONS?.CONTACT_US}>
                <Link to="/contact-us"
                  className={`flex items-center rounded-xl hover:bg-slate-100/70 transition-colors text-xs md:text-sm font-medium group text-slate-600 hover:text-slate-900 ${
                    isSideBarExpand ? 'w-10 h-10 mx-auto justify-center p-0' : 'px-2.5 py-2'
                  }`}
                  rel="noreferrer"
                  onClick={closeMobileSidebar}
                >
                  <img src={"https://stridetech.io/wp-content/uploads/2025/02/Contact-Us.svg"} alt='->' className='flex-shrink-0 w-5 h-5 text-gray-500 transition duration-75 group-hover:text-gray-900' />
                  {!isSideBarExpand && (
                    <span className="flex-1 ml-3 whitespace-nowrap">{content?.LEFT_SIDE_NAV_OPTIONS?.CONTACT_US}</span>
                  )}
                </Link>
              </div>
            </div>

          </div>
          <div
            className={`p-2.5 px-3 flex items-center border-t border-border bg-white/70 ${
              isSideBarExpand ? 'justify-center' : 'justify-between'
            }`}
          >
            {!isSideBarExpand && appData?.latestRelease?.tag_name && <a href={appData?.latestRelease?.url} target='_blank' rel='noreferrer' className='text-xs font-bold leading-tight tracking-tight text-primary bg-primary-soft px-2 py-0.5 rounded-md hover:underline'>
              v{appData?.latestRelease?.tag_name}
            </a>}
            <button
              onClick={toggleSideBar}
              title={isSideBarExpand ? content?.LEFT_SIDE_NAV_OPTIONS?.EXPAND_NAVIGATION : content?.LEFT_SIDE_NAV_OPTIONS?.COLLAPSE_NAVIGATION}
              className={`w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center transition-colors cursor-pointer ${isSideBarExpand ? '' : 'rotate-180'}`}
            >
              <img className='w-[13px] h-[13px]' src={rightArrow} alt="" />
            </button>
          </div>
        </div>
      </aside>
    </>
  )
}

export default Sidenav;
